/**
 * Peças da interface do console.
 *
 * A régua aqui é: destacar o que precisa de atenção e deixar o resto quieto. A
 * tela é instrumento de leitura — quem abre está procurando um número fora do
 * lugar, não navegando.
 */

export function Card({ children, className = '' }) {
  return (
    <div className={`rounded-xl border border-zinc-200 bg-white ${className}`}>{children}</div>
  )
}

export function Titulo({ children, sub }) {
  return (
    <div className="flex flex-col gap-1">
      <h2 className="text-xl font-semibold text-zinc-900">{children}</h2>
      {sub && <p className="max-w-[70ch] text-sm text-zinc-500">{sub}</p>}
    </div>
  )
}

/** Número grande com rótulo. `alerta` pinta de vermelho quando não é zero. */
export function Tile({ n, rotulo, alerta = false }) {
  const acender = alerta && Number(n) > 0
  return (
    <div
      className={`flex flex-col gap-1 rounded-xl border p-4 ${
        acender ? 'border-red-300 bg-red-50' : 'border-zinc-200 bg-white'
      }`}
    >
      <span
        className={`font-mono text-3xl leading-none tabular-nums ${
          acender ? 'text-red-700' : 'text-zinc-900'
        }`}
      >
        {n}
      </span>
      <span className="text-xs text-zinc-500">{rotulo}</span>
    </div>
  )
}

const CORES_DE_PROVEDOR = {
  anthropic: 'text-amber-700 border-amber-300',
  openai: 'text-emerald-700 border-emerald-300',
  groq: 'text-purple-700 border-purple-300',
  google: 'text-blue-700 border-blue-300',
}

export function Provedor({ id }) {
  const cor = CORES_DE_PROVEDOR[id] || 'text-zinc-600 border-zinc-300'
  return (
    <span className={`rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cor}`}>
      {id}
    </span>
  )
}

export function Etiqueta({ tom = 'neutro', children }) {
  const tons = {
    neutro: 'bg-zinc-100 text-zinc-600',
    ok: 'bg-pinho-50 text-pinho-700',
    aviso: 'bg-amber-50 text-amber-700',
    perigo: 'bg-red-50 text-red-700',
  }
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${tons[tom]}`}>
      {children}
    </span>
  )
}

export function Botao({ variante = 'primario', className = '', ...props }) {
  const variantes = {
    primario: 'bg-pinho-500 text-white hover:bg-pinho-600 disabled:bg-zinc-300',
    secundario: 'border border-zinc-300 bg-white text-zinc-700 hover:border-zinc-400 disabled:text-zinc-400',
    perigo: 'border border-red-300 bg-white text-red-700 hover:bg-red-50',
  }
  return (
    <button
      className={`rounded-lg px-3.5 py-2 text-sm font-medium transition disabled:cursor-not-allowed ${variantes[variante]} ${className}`}
      {...props}
    />
  )
}

/**
 * Erro da API, em bloco.
 *
 * `whitespace-pre-line` porque a validação do registro devolve uma linha por
 * problema — juntar tudo num parágrafo esconderia quantos são.
 */
export function Erro({ children }) {
  if (!children) return null
  return (
    <div className="whitespace-pre-line rounded-lg border border-red-200 bg-red-50 p-3 font-mono text-xs text-red-800">
      {children}
    </div>
  )
}

export function Vazio({ children }) {
  return <div className="p-8 text-center text-sm text-zinc-400">{children}</div>
}

/** USD com 2 casas; abaixo de um centavo vira "<0,01" em vez de "0,00". */
export function dinheiro(v) {
  const n = Number(v || 0)
  if (n > 0 && n < 0.01) return '<0,01'
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export function numero(v) {
  return Number(v || 0).toLocaleString('pt-BR')
}
